import { useEffect, useState } from 'react';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { AutoMode, ExpandMore } from '@mui/icons-material';

import { AreYouSureModal, Page } from '../../../../components';
import HeaderBreadcrumbs from '../../../../components/header-breadcrumbs';
import { CustomerService } from '../../../../services/customer.service';
import { CustomerTypes } from '../../../../types';
import { SubscriptionTypes } from '../../../../types/subscription.types';
import { countLicensesAvailable } from '../../../../helpers/licenseUsage';
import { pluralize } from '../../../../helpers/pluralize';

export const SubscriptionListScreen = () => {
    return (
        <Page title={'POM Safe - My Subscriptions'}>
            <Container maxWidth={false}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <HeaderBreadcrumbs
                        heading={'Manage my subscriptions'}
                        links={[
                            {
                                name: 'Subscriptions',
                                href: '/subscriptions',
                            },
                        ]}
                        activeLast={false}
                    />
                </Stack>
                <CustomerSubscriptionList />
            </Container>
        </Page>
    );
};

// CustomerSubscriptionList component takes a user's id and displays their subscriptions
function CustomerSubscriptionList() {
    const [details, setDetails] = useState<CustomerTypes.GetCustomerDetailsResponse>({} as CustomerTypes.GetCustomerDetailsResponse);

    async function setDataAsync() {
        const customerDetails = await CustomerService.getCustomerDetails();
    
        setDetails(customerDetails!);
    }

    useEffect(() => {
        setDataAsync();
    }, []);

    const theme = useTheme();

    // bgcolor is the color of the background
    const bgcolor = theme.palette.primary.light;

    // color is the color of the text
    const color = theme.palette.primary.contrastText;

    return (
        <Box
            sx={{
                bgcolor,
                pt: 2,
                pb: 4,
                color,
                borderRadius: 8,
            }}
        >
            <Box
                sx={{
                    bgcolor,
                    padding: 2,
                    color,
                    borderRadius: 8,
                    margin: 2,
                    fontWeight: 'bold',
                }}
            >
                Showing subscriptions for {details?.customer?.name}
                <p>
                    {' '}
                    {details?.customer?.phoneNumber
                        ? 'Phone Number:' + details?.customer?.phoneNumber
                        : 'No phone number yet'}
                </p>
                <p>
                    <p>
                        {' '}
                        {details?.customer?.contactEmail
                            ? 'Email:' + details?.customer?.contactEmail
                            : 'No email yet'}
                    </p>
                </p>
            </Box>

            {details?.subscriptions?.length > 0 && details?.subscriptions?.map((sub) => {
                return (
                    <SubscriptionCard
                        key={sub.id}
                        {...sub}
                    />
                );
            })}
        </Box>
    );
}

function SubscriptionCard(sub: SubscriptionTypes.Model) {
    const { id, status, inviteCode, expiresAt, subscriptionType } = sub;
    const available = countLicensesAvailable(sub);
    const [expanded, setExpanded] = useState(false);

    const [inviteCodeState, setInviteCodeState] = useState(inviteCode)

    const styleObject = {
        card: { margin: 1 },
        title: {
            fontWeight: 'bold',
        },
        summary: {
            display: 'flex',
            justifyContent: 'space-between',
        },
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    return (
        <Card sx={styleObject.card}>
            <CardContent>
                <p> Subscription Id: {id} </p>
                <p> Invite Code: {inviteCodeState} </p>
                <RefreshInviteCode subscriptionId={id} setInviteCodeState={setInviteCodeState}/>
            </CardContent>

            <CardContent sx={styleObject.title}>
                <Typography variant="body2" color="textSecondary" component="p">
                    Your {subscriptionType} subscription status is {status}, and
                    will expire on {expiresAt.slice(0, 10)}
                </Typography>

                <Typography variant="body2" color="textSecondary" component="p">
                    {available >= 0
                        ? `You have ${pluralize(available, 'license')} available.`
                        : `You are ${pluralize(-available, 'license')} over your limit.`}
                </Typography>

                <Accordion expanded={expanded} >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        onClick={handleExpand}
                        sx={styleObject.summary}
                    ></AccordionSummary>
                    <AccordionDetails sx={{ margin: 1 }}>
                        <Typography> User Management </Typography>
                        <UsersOnSubscriptionList subscriptionId={id} />
                    </AccordionDetails>
                </Accordion>
            </CardContent>
        </Card>
    );
}

type UsersOnSubscriptionListProps = {
    subscriptionId: string;
};

// UsersOnSubscriptionList shows all the users on a subscription, and allows for management of the user - adding or removing them from the subscription
function UsersOnSubscriptionList({
    subscriptionId,
}: UsersOnSubscriptionListProps) {
    return (
        <ul>
            <li> User management is WIP</li>
            <li> User One</li>
            <li> User Two</li>
        </ul>
    );
}

type RefreshInviteCodeProps = {
    subscriptionId: string,
    setInviteCodeState: Function,
}

// RefreshInviteCode refreshes a user's invite code to their subscription
function RefreshInviteCode({subscriptionId, setInviteCodeState}: RefreshInviteCodeProps) {
    const [modalOpen, setModalOpen] = useState(false);

    const refreshHandler = async () => {
        const inviteCode = await CustomerService.resetSubscriptionInviteCode(subscriptionId);
        
        setInviteCodeState(() => inviteCode)
    };
    return (
        <>
            <Button
                variant="contained"
                size="small"
                startIcon={<AutoMode />}
                onClick={() => setModalOpen(true)}
            >
                {' '}
                Refresh Invite Code{' '}
            </Button>
            <AreYouSureModal
                title="Are You Sure You Want to Reset the Invite Code?"
                description="This will reset the invite code. Users will not be able to use the old invite code."
                visible={modalOpen}
                loading={false}
                onConfirm={() => {
                    refreshHandler();
                    setModalOpen(false);
                }}
                onHide={() => {
                    setModalOpen(false);
                }}
            />
        </>
    );
}
